import React, { memo, useCallback, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import plantLogo from "../HomePage/assets/Plant-logo.png";
import Pages from "..";
import { RootState } from "../../redux";
import { TypesAction } from "../../redux/typesAction";
import { usePlantExplorerMobileGate } from "./hooks/usePlantExplorerMobileGate";
import { useExplorerCatalog } from "./hooks/useExplorerCatalog";
import { explorerContentSx, explorerRootSx } from "./explorerTheme";
import ExplorerFarmHeader from "./components/ExplorerFarmHeader";
import ExplorerNameFilterRow from "./components/ExplorerNameFilterRow";
import ExplorerFilterSections from "./components/ExplorerFilterSections";
import ExplorerPromotionBlock from "./components/ExplorerPromotionBlock";
import ExplorerPageSkeleton from "./components/ExplorerPageSkeleton";
import type { ExplorerShadowChip } from "./components/ExplorerShadowChipGrid";

const PlantExplorer: React.FC = () => {
  const isMobile = usePlantExplorerMobileGate();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { auth } = useSelector((state: RootState) => state.auth);

  const { loading, categories, sizes, specials } = useExplorerCatalog(
    isMobile,
    auth?.access_token
  );

  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(
    null
  );
  const [selectedSizeValue, setSelectedSizeValue] = useState<string>("all");

  const handleBack = useCallback(() => {
    if (window.history.length > 1) navigate(-1);
    else navigate("/home");
  }, [navigate]);

  const goNameSearch = useCallback(() => {
    navigate("/plants-explorer/search");
  }, [navigate]);

  /** Điều hướng Plants catalog: server trả list theo `plants` / `sizes` (allPlants API). */
  const goToPlantsCatalog = useCallback(
    (categoryId: number | null, sizeValue: string) => {
      setSelectedCategoryId(categoryId);
      setSelectedSizeValue(sizeValue);

      const params = new URLSearchParams();
      if (categoryId != null) {
        params.set("plants", String(categoryId));
      }
      if (sizeValue !== "all") {
        params.set("sizes", sizeValue);
      }
      const search = params.toString();

      dispatch({ type: TypesAction.CATEGORY_ID, payload: 1 });
      dispatch({ type: TypesAction.SHOW_INPUT_SEARCH, payload: true });

      navigate({
        pathname: "/products-plants",
        search: search ? `?${search}` : "",
      });
    },
    [dispatch, navigate]
  );

  const categoryChips = useMemo((): ExplorerShadowChip[] => {
    const list = categories.filter((c) => c.id !== 0);
    return [
      {
        key: "list",
        label: "List",
        selected: selectedCategoryId === null,
        onClick: () => goToPlantsCatalog(null, selectedSizeValue),
      },
      ...list.map((c) => ({
        key: c.id,
        label: c.name,
        selected: selectedCategoryId === c.id,
        onClick: () => {
          const nextCat = selectedCategoryId === c.id ? null : c.id;
          goToPlantsCatalog(nextCat, selectedSizeValue);
        },
      })),
    ];
  }, [categories, selectedCategoryId, selectedSizeValue, goToPlantsCatalog]);

  const sizeChips = useMemo((): ExplorerShadowChip[] => {
    const opts = sizes.filter((s) => s.id !== 0 && s.value !== "all");
    return [
      {
        key: "all-sizes",
        label: "All Sizes",
        selected: selectedSizeValue === "all",
        onClick: () => goToPlantsCatalog(selectedCategoryId, "all"),
      },
      ...opts.map((s) => ({
        key: s.id,
        label: s.name,
        selected: selectedSizeValue === s.value,
        onClick: () => {
          const nextSize =
            selectedSizeValue === s.value ? "all" : s.value;
          goToPlantsCatalog(selectedCategoryId, nextSize);
        },
      })),
    ];
  }, [sizes, selectedCategoryId, selectedSizeValue, goToPlantsCatalog]);

  if (!isMobile) {
    return null;
  }

  if (loading) {
    return (
      <Pages seo={{ title: "Plant Explorer — TBT Tropical Farm" }}>
        <ExplorerPageSkeleton />
      </Pages>
    );
  }

  return (
    <Pages seo={{ title: "Plant Explorer — TBT Tropical Farm" }}>
      <Box sx={explorerRootSx}>
        <ExplorerFarmHeader logoSrc={plantLogo} onBack={handleBack} />
        <Box sx={explorerContentSx}>
          <ExplorerNameFilterRow
            onOpenNameSearch={goNameSearch}
            onApplyFilters={() =>
              goToPlantsCatalog(selectedCategoryId, selectedSizeValue)
            }
          />
          <ExplorerFilterSections
            categoryChips={categoryChips}
            sizeChips={sizeChips}
          />
          <ExplorerPromotionBlock products={specials} catalogSizes={sizes} />
        </Box>
      </Box>
    </Pages>
  );
};

export default memo(PlantExplorer);
